import { useEffect, useState } from "react";
import type { IdentifyMatch, Species } from "../lib/types";
import { lookupSpecies } from "../lib/api";
import { logEvent } from "../lib/events";
import { hasRealCommonName } from "../../shared/species";
import { openPhoto } from "./PhotoViewer";
import { toast } from "./Toaster";
import { CheckIcon, PhotoFrame, WarningIcon } from "../ui";

// Real iNat taxon ids are always < this; synthetic ids the merge flow
// mints for GBIF/OBIS-only species are above. Kept in sync with
// SYNTHETIC_MIN in worker/factsheet.ts and the client mirror in
// lib/storage.ts.
const SYNTHETIC_TAXON_MIN = 1_000_000_000;

// One match card produced by the identify LLM. `species` is the
// candidate-list hit for this scientific name (if any). When it's
// undefined the match is off-list: the AI named something the location
// search never surfaced. We used to hard-block that path with
// "Not in this location's list" — now we call /api/species/lookup on
// tap, resolve a real Species from iNat / Wikipedia, and pass it up
// through the same onAdd(species) callback so the parent doesn't have
// to know or care.
export function MatchCard({
  match,
  species,
  seen,
  onAdd,
}: {
  match: IdentifyMatch;
  species: Species | undefined;
  seen: boolean;
  onAdd: (species: Species) => void;
}) {
  const pct = Math.round(match.confidence * 100);
  const [resolving, setResolving] = useState(false);
  const offList = !species;

  // Even in-list candidates need enrichment if their data is thin —
  // GBIF/OBIS-only entries come back without a real common name or
  // photo because their sources don't provide those fields. Adding
  // one without enrichment strands the lifelist entry at
  // "Stenella attenuata / Stenella attenuata / no photo". We already
  // have the iNat + Wikipedia lookup path for off-list matches; reuse
  // it here on-demand so the enrichment happens exactly when it's
  // needed (avoids expanding the per-search backfill budget for every
  // long-tail species).
  const candidateNeedsEnrichment =
    !!species &&
    (!hasRealCommonName(species.common_name, species.scientific_name) ||
      !species.photo_url);

  // An off-list match (or a thin candidate) has no photo and often no real
  // common name to show. Pre-resolve it from iNat/Wikipedia when the card
  // mounts so the user sees a photo + real name to PICK from — not only
  // after saving. Best-effort; on failure the card falls back to a
  // placeholder + the AI's name. See offlist-match-no-photo-to-pick.
  const needsPrefetch = offList || candidateNeedsEnrichment;
  const [prefetched, setPrefetched] = useState<Species | null>(null);
  const [prefetching, setPrefetching] = useState(false);
  useEffect(() => {
    if (!needsPrefetch) return;
    let alive = true;
    setPrefetching(true);
    lookupSpecies(match.scientific_name)
      .then((sp) => {
        if (alive) setPrefetched(sp);
      })
      .catch(() => {
        /* best-effort; render falls back to placeholder + AI name */
      })
      .finally(() => {
        if (alive) setPrefetching(false);
      });
    return () => {
      alive = false;
    };
  }, [needsPrefetch, match.scientific_name]);

  // Photo + common name to display, in priority order: the in-list
  // candidate, then the pre-resolved species, then the AI's own guess.
  const photoUrl = species?.photo_url || prefetched?.photo_url || "";
  // Name priority: the in-list candidate's curated name, then the AI's own
  // common name for THIS identification (what the user searched for and is
  // confirming — keeping it means the name doesn't change out from under
  // them after they pick), then the looked-up canonical name as a fallback
  // only when the AI gave no real common name, then scientific. The lookup
  // still supplies the photo regardless. See identify-name-changes-after-pick.
  const displayCommon =
    species && hasRealCommonName(species.common_name, species.scientific_name)
      ? species.common_name
      : hasRealCommonName(match.common_name, match.scientific_name)
        ? match.common_name
        : prefetched &&
            hasRealCommonName(prefetched.common_name, prefetched.scientific_name)
          ? prefetched.common_name
          : match.common_name || match.scientific_name;

  const onClickAdd = async () => {
    // Fast path: candidate is complete, no enrichment needed.
    if (species && !candidateNeedsEnrichment) {
      onAdd(species);
      return;
    }
    setResolving(true);
    try {
      // Reuse the pre-resolved species from the mount effect when we have
      // it, so tapping doesn't pay for a second lookup.
      const resolved =
        prefetched ?? (await lookupSpecies(match.scientific_name));
      // Save the name the user actually confirmed: prefer the AI's common
      // name for this identification so the saved entry matches the card
      // they picked (no surprise rename to a different canonical name).
      // Fall back to the looked-up name only when the AI gave no real
      // common name. See identify-name-changes-after-pick.
      const common_name = hasRealCommonName(
        match.common_name,
        match.scientific_name,
      )
        ? match.common_name
        : hasRealCommonName(resolved.common_name, resolved.scientific_name)
          ? resolved.common_name
          : resolved.common_name;
      // Merge: prefer a real iNat taxon_id from the candidate when we
      // have one (keeps sighting-level identity stable across
      // sessions), but take the resolved common_name / photo / category
      // since those are the fields the lookup just enriched.
      const merged: Species =
        species && species.taxon_id < SYNTHETIC_TAXON_MIN
          ? { ...resolved, common_name, taxon_id: species.taxon_id }
          : { ...resolved, common_name };
      logEvent(offList ? "fish.add.off_list" : "fish.add.enriched", {
        scientific_name: match.scientific_name,
        candidate_common: species?.common_name ?? "",
        candidate_had_photo: !!species?.photo_url,
        resolved_common: resolved.common_name,
        source_confidence: match.confidence,
        resolved_taxon_id: resolved.taxon_id,
        used_taxon_id: merged.taxon_id,
        has_photo: !!merged.photo_url,
      });
      onAdd(merged);
    } catch (e) {
      // If the enrichment fails but we had a working (if thin)
      // candidate, don't block the log — fall through with what we
      // have. Purely off-list misses still surface a toast.
      if (species) {
        onAdd(species);
        logEvent("fish.add.enriched.miss", {
          scientific_name: match.scientific_name,
          message: e instanceof Error ? e.message : String(e),
        });
        return;
      }
      toast(
        e instanceof Error
          ? e.message
          : "Couldn't find that species — try adding it manually later.",
      );
      logEvent("fish.add.off_list.miss", {
        scientific_name: match.scientific_name,
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="card p-4">
      <div className="flex items-start gap-3">
        {photoUrl ? (
          <button
            type="button"
            onClick={() =>
              openPhoto({
                url: photoUrl,
                common_name: displayCommon,
                scientific_name: match.scientific_name,
              })
            }
            className="h-14 w-14 shrink-0 overflow-hidden rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua-deep"
            aria-label={`Enlarge photo of ${displayCommon}`}
          >
            <PhotoFrame
              src={photoUrl}
              alt={displayCommon}
              hint={match.scientific_name}
              className="h-14 w-14"
              radiusClass="rounded-lg"
            />
          </button>
        ) : (
          <PhotoFrame
            hint={match.scientific_name}
            className={`h-14 w-14 flex-none ${prefetching ? "animate-pulse" : ""}`}
            radiusClass="rounded-lg"
          />
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="truncate font-semibold text-ink">
              {displayCommon}
            </p>
            <span className="shrink-0 text-sm font-bold tabular-nums text-aqua-deep">
              {pct}%
            </span>
          </div>
          <p className="truncate text-sm italic text-meta">
            {match.scientific_name}
          </p>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-ink/10">
            <div
              className="h-full rounded-full bg-aqua-deep"
              style={{ width: `${pct}%` }}
            />
          </div>
          {offList && (
            <p className="mt-1 text-[11px] text-meta">
              Not in this location's list — we'll pull it from iNat / Wikipedia
              on save.
            </p>
          )}
        </div>
      </div>

      <p className="mt-3 text-sm text-ink">{match.reasoning}</p>
      {match.caveat && (
        <p className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-coral/10 px-3 py-2 text-xs text-coral">
          <WarningIcon className="mt-0.5 h-4 w-4 flex-none" />
          <span>{match.caveat}</span>
        </p>
      )}

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={onClickAdd}
          disabled={seen || resolving}
          className={seen ? "btn-ghost" : "btn-accent"}
        >
          {seen ? (
            <>
              <CheckIcon className="h-4 w-4" />
              In your lifelist
            </>
          ) : resolving ? (
            "Looking up…"
          ) : (
            "I saw this one"
          )}
        </button>
      </div>
    </div>
  );
}
